<script setup>
import draggable from 'vuedraggable';
import { useTestsStore, useQuestionsStore, useAnswersStore } from '@stores';
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

import Answers from '../components/Answers.vue';
import Editor from '../components/Editor';
import { useI18n } from 'vue-i18n';
import { generateHTML } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';

const { t } = useI18n({ useScope: 'global' });
const route = useRoute();

const testId = route.params.testId;
const questionId = route.query.question;
const testsStore = useTestsStore();
const questionsStore = useQuestionsStore();
const answersStore = useAnswersStore();

const previousVal = ref({});

const addQuestionHandler = () => {
    questionsStore.addQuestion();
};

onMounted(async () => {
    await questionsStore.getQuestions(testId);
    if (questionsStore.allQuestions.length) {
        await questionsStore.selectQuestion(questionId || questionsStore.allQuestions[0].question_id);
    } else {
        await questionsStore.addQuestion();
    }
});

const updateOrderHandler = (event) => {
    questionsStore.updateQuestionOrder(event.newIndex);
};

const setOriginalValue = (title, language) => {
    previousVal.value[language] = title;
};

const updateQuestionTitle = async (question) => {
    const prevValue = previousVal.value[question.language];
    if (prevValue === question.title) return;
    await questionsStore.updateQuestion(question);
};

const updateQuestionHandler = async (question) => {
    await questionsStore.updateQuestion(question);
    await questionsStore.getQuestion(questionsStore.selectedId);
    if (!questionsStore.question.answers.length) {
        await answersStore.addAnswer();
    }
};

const dragOptions = computed(() => ({
    animation: 400,
    group: 'description',
    disabled: false,
    ghostClass: 'ghost',
}));

const selectQuestion = (id) => {
    questionsStore.selectQuestion(id);
};

const getTranslatedLabel = (id) => {
    const option = testsStore.main.types.find((item) => item.id === id);
    return option ? t(`tests.types.${option.name}`) : '';
};
</script>

<template>
    <div class="question-constructor">
        <div class="question-sidebar">
            <Button icon="pi pi-plus" :label="t('tests.addQuestion')" fluid @click="addQuestionHandler" />
            <draggable :scroll-sensitivity="200" :force-fallback="true" v-model="questionsStore.allQuestions" item-key="question_id" v-bind="dragOptions" @end="updateOrderHandler" handle=".drag-handle" class="question-items">
                <template #item="{ element, index }">
                    <Card class="question-item" :data-id="element.question_id" :class="{ active: questionsStore.selectedId === element.question_id }" @click="selectQuestion(element.question_id)">
                        <template #content>
                            <div class="question-item-top">
                                <span>{{ t('tests.question') }} #{{ index + 1 }}</span>
                                <div class="question-item-buttons">
                                    <Button icon="pi pi-times" variant="text" @click.stop="questionsStore.deleteQuestion(element)" severity="danger" />
                                    <Button icon="pi pi-copy" variant="text" @click="questionsStore.duplicateQuestion(element)" />
                                    <Button icon="pi pi-arrows-alt" variant="text" class="drag-handle" />
                                </div>
                            </div>
                            <ul class="list-none">
                                <li v-for="item in element.titles" :key="item.language" class="question-item-title">
                                    <span v-if="item.title && testsStore.main.selectedLanguages?.length > 1">{{ item.language.toUpperCase() }} -</span>

                                    <div v-if="item.title" v-html="generateHTML(item.title, [StarterKit])"></div>
                                </li>
                            </ul>
                        </template>
                    </Card>
                </template>
            </draggable>
        </div>

        <div class="test-content" v-if="questionsStore.allQuestions.length">
            <div class="input-wrapper">
                <label for="question">{{ t('tests.enterQuestion') }}</label>
                <div class="question-editor-wrapper" v-for="title in questionsStore.question.titles" :key="title">
                    <span class="question-language" v-if="testsStore.main.selectedLanguages.length > 1">
                        {{ title.language.toUpperCase() }}
                    </span>
                    <Editor v-model="title.title" class="question-editor flex-1" @blur="updateQuestionTitle({ language: title.language, title: title.title })" @focus="setOriginalValue(title.title, title.language)" />
                </div>
            </div>

            <div class="input-wrapper">
                <label for="question-type">{{ t('tests.questionType') }}</label>
                <Select id="question-type" v-model="questionsStore.question.type" :options="testsStore.main.types" optionLabel="name" optionValue="id" :placeholder="t('tests.selectType')" fluid @change="updateQuestionHandler({ type: questionsStore.question.type })">
                    <template #value="slotProps">
                        <span v-if="slotProps.value">
                            {{ getTranslatedLabel(slotProps.value) }}
                        </span>
                        <span v-else>
                            {{ slotProps.placeholder }}
                        </span>
                    </template>
                    <template #option="slotProps">
                        {{ t(`tests.types.${slotProps.option.name}`) }}
                    </template>
                </Select>
            </div>

            <Divider />

            <Answers v-if="questionsStore.question.answers?.length" />

            <Button v-if="questionsStore.question.type != 3" :label="t('tests.addAnswer')" icon="pi pi-plus" @click="answersStore.addAnswer" />

            <div class="answer-multiple" v-if="questionsStore.question.type == 1 || questionsStore.question.type == 2">
                <Checkbox v-model="questionsStore.question.isMultiple" :binary="true" @change="updateQuestionHandler({ isMultiple: questionsStore.question.isMultiple })" inputId="answer-multiple" />

                <label for="answer-multiple">{{ t('tests.isMultiple') }}</label>
            </div>
        </div>
    </div>
</template>

<style scoped>
.question-constructor {
    @apply flex gap-4 items-start;
}
.question-sidebar {
    @apply w-[300px] flex flex-col gap-4;
    /* overflow-y-auto h-[calc(100vh_-240px)] sticky top-[70px] rounded-xl */
}
.question-items {
    @apply flex flex-col gap-2;
}
.question-item {
    @apply cursor-pointer rounded-xl shadow-lg border-2 border-white;
}
.question-item.active {
    @apply border-black bg-gray-100;
}
.ghost {
    @apply bg-gray-100 border-2 border-black;
}
.question-item-top {
    @apply flex items-center justify-between w-full;
}
.question-item-buttons {
    @apply flex items-center text-gray-400;
}
.question-item .p-inputtext {
    @apply border-none py-2 px-0 shadow-none;
}
.question-item-title {
    @apply truncate flex items-center gap-2;
}
.test-content {
    @apply flex-1 p-12 bg-white flex items-start flex-col gap-4 rounded-xl shadow-lg;
}
.input-wrapper {
    @apply flex flex-col gap-2 w-full;
}
.answer-multiple {
    @apply flex items-center gap-2 mt-2;
}
.question-editor-wrapper {
    @apply flex items-stretch border border-surface-300 rounded-md w-full;
}
.question-language {
    @apply p-2 border-r border-surface-300 flex items-center;
}
.question-editor {
    @apply p-2 w-full;
}
</style>
